import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HomeComponent} from './components/home/home.component';
import {LoginComponent} from './components/login/login.component';
import { UserInviteComponent } from './components/user/user-invite/user-invite.component';
import { MfaEnrolComponent } from './components/auth/mfa-enrol/mfa-enrol.component';
import { AcceptInvitationComponent } from './components/auth/accept-invitation/accept-invitation.component';
import { ForgotPasswordComponent } from './components/auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import {HeaderComponent} from './components/header/header.component';
import {TranslatePipe, TranslateDirective, provideTranslateService} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {APP_VERSION} from './app-version';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {LeftMenuComponent} from './components/left-menu/left-menu.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {SharedService} from './services/sharedService';
import {NgxBootstrapSliderModule} from 'ngx-bootstrap-slider';
import {BaseService} from './services/base.service';
import {BsModalService, ModalModule, MODAL_CONFIG_DEFAULT_OVERRIDE} from 'ngx-bootstrap/modal';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {BaseChartDirective, provideCharts, withDefaultRegisterables} from 'ng2-charts';
import {NgxSpinnerModule} from 'ngx-spinner';
import {BarChartComponent} from './components/charts/bar-chart/bar-chart.component';
import {DoughnutChartComponent} from './components/charts/doughnut-chart/doughnut-chart.component';
import {RadarChartComponent} from './components/charts/radar-chart/radar-chart.component';
import {PieChartComponent} from './components/charts/pie-chart/pie-chart.component';
import {MultiAxisChartComponent} from './components/charts/multi-axis-chart/multi-axis-chart.component';
import {StackedChartComponent} from './components/charts/stacked-chart/stacked-chart.component';
import {ServiceListComponent} from './components/service/service-list.component';
import {ServiceEditComponent} from './components/service/service-edit/service-edit.component';
import {DatatableComponent} from './components/datatable/datatable.component';
import {AddButtonComponent} from './components/function-buttons/add-button.component';
import {SaveButtonComponent} from './components/function-buttons/save-button.component';
import {ServiceService} from './services/service.service';
import {ServiceTypeDirective} from './directives/service-type.directive';
import {DashboardTypeDirective} from './directives/dashboard-type.directive';
import {CurrencyService} from './services/currency.service';
import {CurrencyRateService} from './services/currency-rate.service';
import {BankService} from './services/bank.service';
import {TasService} from './services/tas.service';
import {ServiceCsvComponent} from './components/service/service-type-forms/csv-importer/service-csv.component';
import {ServiceMcasComponent} from './components/service/service-type-forms/service-mcas.component';
import {CsvUploadComponent} from './components/service/service-type-forms/csv-importer/csv-upload.component';
import {ServiceOtpComponent} from './components/service/service-type-forms/service-otp.component';
import {CurrencyListComponent} from './components/currency/currency-list.component';
import {CurrencyAddEditComponent} from './components/currency/currency-add-edit.component';
import {CurrencyRateListComponent} from './components/currency-rate/currency-rate-list.component';
import {CsvListComponent} from './components/csvlist/csvlist.component';
import {CsvListService} from './services/csvList.service';
import {DashboardConfigComponent} from './components/dashboard-config/dashboard-config.component';
import {DashboardEditComponent} from './components/dashboard-config/dashboard-edit/dashboard-edit.component';
import {DashboardService} from './services/dashboard.service';
import {BatchProcessListComponent} from './components/batch-process-manager/batch-process-list.component';
import {BatchProcessViewComponent} from './components/batch-process-manager/batch-process-view.component';
import {BatchProcessService} from './services/batchProcess.service';
/*
 * Icons: Lucide, per the RDF design system (2px stroke, square, sized in px).
 *
 * This replaced ngx-bootstrap-icons, which was registered with `allIcons` --
 * every Bootstrap Icon there is, bundled to draw thirty-six of them. Only the
 * icons the templates actually use are listed here, so adding one is a
 * deliberate two-line change and the bundle carries nothing else.
 */
import {LucideAngularModule, ArrowRight, Bell, Bug, Building2, ChartLine, ChartPie, Check, ChevronDown, ChevronRight, ChevronUp, CircleCheck, CircleQuestionMark, CloudDownload, CloudUpload, Cpu, Dot, FileSpreadsheet, FileText, Funnel, Image, Info, LayoutGrid, List, Lock, LogOut, NotebookText, PanelLeft, Plus, RefreshCw, Settings, SquarePen, Star, Trash2, TrendingUp, TriangleAlert, User, X} from 'lucide-angular';
import {AccordionModule} from 'ngx-bootstrap/accordion';
import {AuthService} from './services/auth.service';
import {JwtModule} from '@auth0/angular-jwt';
import {AuthInterceptor} from './util/auth.interceptor';
import {AuthGuard} from './guards/auth.guard';
import {UserListComponent} from './components/user/user-list.component';
import {UserService} from './services/user.service';
import {UserAddEditComponent} from './components/user/user-add-edit/user-add-edit.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {PeriodRangeComponent} from './components/charts/period-range/period-range.component';
import {LandingPageComponent} from './components/landing-page/landing-page.component';
import {AdminComponent} from './components/admin/admin.component';
import {AdminService} from './services/admin.service';
import {ContractComponent} from './components/contract/contract.component';
import {ContractService} from './services/contract.service';
import {ExcelService} from './services/excel.Service';
import {IndicatorService} from './services/indicator.service';
import {IndicatorAddEditComponent} from './components/indicator/indicator-add-edit.component';
import {IndicatorListComponent} from './components/indicator/indicator-list.component';
import {MultipleSelectComponent} from './components/custom-ui-elements/multiple-select.component';
import {UserAvatarComponent} from './components/shared/user-avatar/user-avatar.component';
import {NgSelectModule} from '@ng-select/ng-select';
import {CompanyComponent} from './components/company/company.component';
import {CompanyService} from './services/company.service';
import {CompanyAddEditComponent} from './components/company/company-add-edit/company-add-edit.component';
import {DatasetsComponent} from './components/company/datasets/datasets.component';
import {TaxDivisionService} from './services/taxDivision.service';
import {RiskManagementComponent} from './components/risk-management/risk-management.component';
import {IndicatorSectorsComponent} from './components/indicator-sectors/indicator-sectors.component';
import {
    IndicatorSectorAddEditComponent
} from './components/indicator-sectors/indicator-sector-add-edit/indicator-sector-add-edit.component';
import {IndicatorSectorService} from './services/indicatorSector.service';
import {PayeeSectorsComponent} from './components/payee-sectors/payee-sectors.component';
import {PayeeSectorService} from './services/payeeSector.service';
import {
    PayeeSectorAddEditComponent
} from './components/payee-sectors/payee-sector-add-edit/payee-sector-add-edit.component';
import {RiskResultsComponent} from './components/indicator/risk-results/risk-results.component';
import {RiskBreakdownComponent} from './components/indicator/risk-breakdown/risk-breakdown.component';
import {RiskScoreListComponent} from './components/indicator/risk-score-list/risk-score-list.component';
import {BaseComponentComponent} from './components/base-component/base-component.component';
import {RegionalOfficerComponent} from './components/regional-officer/regional-officer.component';
import {ControlTeamListComponent} from './components/control-team/control-team-list.component';
import {
    ControlTeamAddEditComponent
} from './components/control-team/control-team-add-edit/control-team-add-edit.component';
import {ControlTeamService} from './services/controlTeam.service';
import {ControlModuleComponent} from './components/control-module/control-module.component';
import {
    ControlModuleHeaderComponent
} from './components/control-module/control-module-header/control-module-header.component';
import {NotificationService} from './services/notification.service';
import {NotificationsComponent} from './components/control-module/notifications/notifications.component';
import {StepTabberComponent} from './components/control-module/step-tabber/step-tabber.component';
import {FormComponent} from './components/control-module/form/form.component';
import {StepComponent} from './components/control-module/step/step.component';
import {ActionElementComponent} from './components/control-module/action-element/action-element.component';
import {RichTextElementComponent} from './components/control-module/rich-text-element/rich-text-element.component';
import {
    FileUploadElementComponent
} from './components/control-module/file-upload-element/file-upload-element.component';
import {NewProcessComponent} from './components/control-module/new-process/new-process.component';
import {ProcessCardComponent} from './components/control-module/process-card/process-card.component';
import {ProcessService} from './services/process.service';
import {ProcessComponent} from './components/control-module/process/process.component';
import {CommentsComponent} from './components/control-module/comments/comments.component';
import {CommentService} from './services/comment.service';
import {TimelineComponent} from './components/control-module/timeline/timeline.component';
import {SelectComponent} from './components/control-module/form/select/select.component';
import {RadioComponent} from './components/control-module/form/radio/radio.component';
import {CheckboxComponent} from './components/control-module/form/checkbox/checkbox.component';
import {TableComponent} from './components/control-module/form/table/table.component';
import {ToastService} from './services/toast-service';
import {ToastContainerComponent} from './components/control-module/toast-container/toast-container.component';
import {DynamicTableComponent} from './components/control-module/form/dynamic-table/dynamic-table.component';
import {CalendarComponent} from './components/control-module/form/calendar/calendar.component';
import {ControlModuleGuard} from './guards/control.module.guard';
import {NewPreprocessComponent} from './components/new-preprocess/new-preprocess.component';
import {RiskService} from './services/riskService';
import {IfuService} from './services/ifuService';
import {ProposalComponent} from './components/proposal/proposal.component';
import {DateRangePickerComponent} from './components/date-range-picker/date-range-picker.component';
import {TimesheetComponent} from './components/control-module/timesheet/timesheet.component';
import {TimesheetTableComponent} from './components/control-module/timesheet-table/timesheet-table.component';
import {ThousandSeparatorDirective} from './directives/thousandSeparator.directive';
import {ThousandSeparatorPipe} from './pipes/thousand-separator.pipe';
import {OnlyNumber} from './directives/onlyNumberDirective';
import {DocumentsComponent} from './components/control-module/documents/documents.component';
import {
    ActionTimesheetComponent
} from './components/control-module/timesheet/action-timesheet/action-timesheet.component';
import {
    FusionFileFormatComponent
} from './components/service/service-type-forms/fusion-file-format/fusion-file-format.component';
import { SpecialColumnsEditorComponent } from './components/service/service-type-forms/special-columns-editor/special-columns-editor.component';
import {ModalComponent, ModalDirectivesDirective} from "./components/modal/modal.component";
import {CompanyFormComponent} from "./components/company/company-form.component";
import {ToastrModule} from "ngx-toastr";
import {NgxTimelineModule} from "@frxjs/ngx-timeline";
import {ReportListComponent} from "./components/report/report-list.component";
import {ReportAddEditComponent} from "./components/report/report-add-edit.component";
import {ReportService} from "./services/report.service";
import {CompositeFilterComponent} from "./components/composite-filter/composite-filter.component";
import {CompanyDocumentUploadComponent} from "./components/company/company-add-edit/company-document-upload.component";
import {DocumentService} from "./services/document.service";
import {FileUploadModule as Ng2FileUploadModule} from 'ng2-file-upload';
import {FileUploadModule} from 'primeng/fileupload';
import {providePrimeNG} from 'primeng/config';
import {definePreset} from '@primeng/themes';
import Lara from '@primeng/themes/lara';

// The RDF navy ramp, exactly as _rdf-tokens.scss defines it. 950 is the one
// step the token file does not carry: PrimeNG requires it, so it continues the
// ramp past --navy-900.
const RdfLara = definePreset(Lara, {
    semantic: {
        primary: {
            50: '#f7f7f9',
            100: '#eaecf1',
            200: '#d1d5e0',
            300: '#a6b0c4',
            400: '#7c8aa8',
            500: '#52648b',
            600: '#2C4272',
            700: '#253760',
            800: '#1e2d4e',
            900: '#162139',
            950: '#0f1626'
        }
    }
});

import {TemplateEditor} from "./components/template-editor/template-editor.component";
import {HelpEditorComponent} from "./components/help/help-editor.component";
import {HelpComponent} from "./components/help/help.component";
import { SourceService } from './services/sourceService';
import {ExternalApiGatewayService} from "./services/external-api-gateway.service";
import {PortalDashboardGroupComponent} from './components/portal-dashboard-group/portal-dashboard-group.component';
import {
  PortalDashboardGroupEditComponent
} from './components/portal-dashboard-group/portal-dashboard-group-edit/portal-dashboard-group-edit.component';
import {PortalDashboardGroupService} from './services/portalDashboardGroup.service';
import {PortalContentComponent} from './components/portal-content/portal-content.component';
import {PortalNewsletterComponent} from './components/portal-newsletter/portal-newsletter.component';
import {IssueTrackerComponent} from './components/issue-tracker/issue-tracker.component';
import {ReleaseNotesComponent} from './components/release-notes/release-notes.component';
import {UserProfileComponent} from './components/user-profile/user-profile.component';
import {IssueTrackerSettingsComponent} from './components/issue-tracker-settings/issue-tracker-settings.component';
import {PortalNewsletterService} from './services/portalNewsletter.service';
import {IssueTrackerService} from './services/issueTracker.service';
import {ExternalApiGatewayListComponent} from "./components/external-api-gateway/external-api-gateway-list.component";
import {
    ExternalApiGatewayEditComponent
} from "./components/external-api-gateway/external-api-gateway-edit/external-api-gateway-edit.component";

export function tokenGetter() {
    return localStorage.getItem('access_token');
}

@NgModule({ declarations: [
        AppComponent,
        HomeComponent,
        LoginComponent,
        MfaEnrolComponent,
        UserInviteComponent,
        AcceptInvitationComponent,
        ForgotPasswordComponent,
        ResetPasswordComponent,
        HeaderComponent,
        LeftMenuComponent,
        BarChartComponent,
        DoughnutChartComponent,
        RadarChartComponent,
        PieChartComponent,
        MultiAxisChartComponent,
        AddButtonComponent,
        SaveButtonComponent,
        StackedChartComponent,
        ServiceListComponent,
        ServiceEditComponent,
        DatatableComponent,
        ServiceTypeDirective,
        DashboardTypeDirective,
        ServiceCsvComponent,
        ServiceMcasComponent,
        CsvUploadComponent,
        ServiceOtpComponent,
        CurrencyListComponent,
        CurrencyAddEditComponent,
        CurrencyRateListComponent,
        CsvListComponent,
        DashboardConfigComponent,
        DashboardEditComponent,
        IndicatorListComponent,
        ReportListComponent,
        OnlyNumber,
        IndicatorAddEditComponent,
        ReportAddEditComponent,
        BatchProcessListComponent,
        BatchProcessViewComponent,
        UserListComponent,
        UserAddEditComponent,
        PeriodRangeComponent,
        LandingPageComponent,
        AdminComponent,
        ContractComponent,
        MultipleSelectComponent,
        UserAvatarComponent,
        CompanyComponent,
        CompanyAddEditComponent,
        DatasetsComponent,
        RiskManagementComponent,
        IndicatorSectorsComponent,
        IndicatorSectorAddEditComponent,
        PayeeSectorsComponent,
        PayeeSectorAddEditComponent,
        RiskResultsComponent,
        RiskBreakdownComponent,
        RiskScoreListComponent,
        BaseComponentComponent,
        RegionalOfficerComponent,
        ControlTeamListComponent,
        ControlTeamAddEditComponent,
        ControlModuleComponent,
        ControlModuleHeaderComponent,
        NotificationsComponent,
        StepTabberComponent,
        ThousandSeparatorDirective,
        ThousandSeparatorPipe,
        FormComponent,
        StepComponent,
        ActionElementComponent,
        RichTextElementComponent,
        FileUploadElementComponent,
        NewProcessComponent,
        ProcessCardComponent,
        ProcessComponent,
        CommentsComponent,
        TimelineComponent,
        SelectComponent,
        RadioComponent,
        CheckboxComponent,
        TableComponent,
        ToastContainerComponent,
        DynamicTableComponent,
        CalendarComponent,
        NewPreprocessComponent,
        ProposalComponent,
        DateRangePickerComponent,
        TimesheetComponent,
        TimesheetTableComponent,
        DocumentsComponent,
        ActionTimesheetComponent,
        FusionFileFormatComponent,
        SpecialColumnsEditorComponent,
        ModalComponent,
        ModalDirectivesDirective,
        CompanyFormComponent,
        CompositeFilterComponent,
        CompanyDocumentUploadComponent,
        TemplateEditor,
        HelpEditorComponent,
        HelpComponent,
        ExternalApiGatewayListComponent,
        ExternalApiGatewayEditComponent,
        PortalDashboardGroupComponent,
        PortalDashboardGroupEditComponent,
        PortalContentComponent,
        PortalNewsletterComponent,
        IssueTrackerComponent,
        IssueTrackerSettingsComponent,
        ReleaseNotesComponent,
        UserProfileComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        NgbModule,
        BaseChartDirective,
        FormsModule,
        ReactiveFormsModule,
        NgxSpinnerModule,
        BrowserAnimationsModule,
        NgxBootstrapSliderModule,
        NgSelectModule,
        LucideAngularModule.pick({ArrowRight, Bell, Bug, Building2, ChartLine, ChartPie, Check, ChevronDown, ChevronRight, ChevronUp, CircleCheck, CircleQuestionMark, CloudDownload, CloudUpload, Cpu, Dot, FileSpreadsheet, FileText, Funnel, Image, Info, LayoutGrid, List, Lock, LogOut, NotebookText, PanelLeft, Plus, RefreshCw, Settings, SquarePen, Star, Trash2, TrendingUp, TriangleAlert, User, X}),
        BsDatepickerModule,
        AccordionModule,
        // VerticalTimelineModule,
        ModalModule,
        PaginationModule,
        BsDropdownModule,
        ButtonsModule,
        Ng2FileUploadModule,
        FileUploadModule,
        ToastrModule.forRoot({
            preventDuplicates: true,
            progressBar: true
            // positionClass: 'inline'
        }),
        JwtModule.forRoot({
            config: {
                tokenGetter
            }
        }),
        NgxTimelineModule,
        TranslatePipe,
        TranslateDirective,
        LucideAngularModule], providers: [
        AdminService, AuthService, AuthGuard, ControlModuleGuard, BankService, BaseService, BatchProcessService, BsModalService,
        CommentService, CompanyService, ContractService, ControlTeamService, CsvListService, CurrencyService, CurrencyRateService,
        DashboardService, ExcelService, IndicatorSectorService, IndicatorService, ReportService, NotificationService, PayeeSectorService,
        ProcessService, RiskService, IfuService, SourceService, ServiceService, SharedService, TasService, TaxDivisionService, ToastService, UserService,
        ThousandSeparatorPipe, DocumentService,
        ProcessService, RiskService, IfuService, ServiceService, SharedService, TasService, TaxDivisionService, ToastService, UserService,
        ThousandSeparatorPipe, DocumentService, ExternalApiGatewayService, PortalDashboardGroupService, PortalNewsletterService, IssueTrackerService,
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: MODAL_CONFIG_DEFAULT_OVERRIDE, useValue: { keyboard: false, backdrop: 'static' } },
        provideHttpClient(withInterceptorsFromDi()),
        provideCharts(withDefaultRegisterables()),
        // Pin primeng to light mode. Its default darkModeSelector is 'system',
        // which emits an @media (prefers-color-scheme: dark) block that sets
        // color-scheme: dark globally — turning bare inputs (e.g. the control-module
        // search box) black on dark-OS. Scoping to a class the app never applies
        // keeps this light-themed app light.
        /*
         * PrimeNG is used for one control (the file uploader), but it ships a
         * whole design system's variables. Stock Lara's primary is a teal that
         * belongs to no part of this application, so the preset is re-pointed at
         * the RDF navy ramp -- the same values as src/styles/_rdf-tokens.scss.
         * Without this, the uploader's buttons and focus rings were a third
         * colour on a screen that already had the brand navy on it.
         */
        providePrimeNG({ theme: { preset: RdfLara, options: { darkModeSelector: '.app-dark' } } }),
        provideTranslateService({
            // The version is a cache buster, not decoration: the translation
            // bundles are the one thing Angular does not fingerprint, so
            // without it a browser can serve last release's words beside this
            // release's screens. See app-version.ts.
            loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json?v=' + APP_VERSION })
        })
    ] })
export class AppModule {
}
